'use client'

// frontend/src/components/h2r/RampAlerts.tsx
//
// The notification that tells the room something happened.
//
// Two events are worth interrupting somebody for, and they are not the same
// event: a commitment ARRIVED, and a commitment now NEEDS A DECISION. A ramp
// reaches the second one minutes after the first, and again later for its second
// gate, so watching only for new case refs meant the approval that actually
// wanted a person never announced itself.
//
// Mounted in the app shell rather than on the dashboard, because the moment a
// decision opens is exactly the moment somebody is looking at another page.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { Icons } from '@/components/ui/icons'
import { fetchRampCases } from '@/hooks/useRampCases'
import { isForbidden } from '@/lib/api-client'
import type { HRCase } from '@/components/hr-case-management/types/case.types'

/**
 * How often the Command Center asks whether anything happened.
 *
 * Four seconds, not ten. Auto's own mailbox poll already costs up to twenty
 * seconds before a commitment is pushed at all, so every second the browser
 * adds on top is one the room watches a static screen. This request is a small
 * JSON read against a local API.
 */
const POLL_MS = 4_000

export type AlertKind = 'arrival' | 'approval'

export interface RampAlert {
  id: string
  kind: AlertKind
  caseRef: string
  subject: string
  /** Set once, when the alert is raised, so the bell can order and age them. */
  at: number
  read: boolean
}

interface RampAlertsValue {
  alerts: RampAlert[]
  unread: number
  dismiss: (id: string) => void
  markAllRead: () => void
}

const RampAlertsContext = createContext<RampAlertsValue | null>(null)

/**
 * The ramp alerts, for anything that wants to show them.
 *
 * Returns an empty set outside the provider rather than throwing: the bell lives
 * in the header, which also renders on pages that never mount this provider.
 */
export function useRampAlerts(): RampAlertsValue {
  return (
    useContext(RampAlertsContext) ?? {
      alerts: [],
      unread: 0,
      dismiss: () => {},
      markAllRead: () => {},
    }
  )
}

function workbenchHref(caseRef: string): string {
  return `/hr-case-management?case=${encodeURIComponent(caseRef)}`
}

/** The decision this case is waiting on, or null. */
function openDecisionId(c: HRCase): string | null {
  const loose = c as HRCase & { decisionId?: unknown }
  const direct = typeof loose.decisionId === 'string' ? loose.decisionId.trim() : ''
  if (direct) return direct
  return c.status === 'in_workbench' ? `status:${c.caseRef}` : null
}

/**
 * A desktop notification, when the browser has already agreed to them.
 *
 * Never asks here and never throws: a denied permission, a missing API or a
 * permissions policy all end the same way, with the on-screen card doing the job.
 */
function notifyIfPermitted(alert: RampAlert): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return
  if (Notification.permission !== 'granted') return
  try {
    new Notification(
      alert.kind === 'approval' ? 'Your approval is needed' : 'New commitment arrived',
      { body: `${alert.caseRef} — ${alert.subject}`, tag: `${alert.kind}-${alert.caseRef}` }
    )
  } catch {
    // The card on screen has already done the job.
  }
}

function AlertCard({ alert, onDismiss }: { alert: RampAlert; onDismiss: () => void }) {
  const approval = alert.kind === 'approval'
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 24, scale: 0.97 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 24, scale: 0.97 }}
      transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
      role='status'
      aria-live='polite'
      className='w-[21rem] rounded-2xl bg-brand-navy p-4 shadow-float-lg'
    >
      <div className='flex items-start gap-3'>
        <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/10'>
          <Icons.alertCircle
            className={approval ? 'h-4 w-4 text-[#F0A05A]' : 'hidden'}
            strokeWidth={1.5}
          />
          <Icons.mail
            className={approval ? 'hidden' : 'h-4 w-4 text-brand-cornflower'}
            strokeWidth={1.5}
          />
        </div>
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-semibold text-white'>
            {approval ? 'Your approval is needed' : 'New commitment arrived'}
          </p>
          <p className='mt-1 line-clamp-2 text-[13px] leading-relaxed text-white/75'>
            {alert.subject}
          </p>
          <p className='mt-1 text-[13px] text-white/45'>{alert.caseRef}</p>
          <Link
            href={workbenchHref(alert.caseRef)}
            onClick={onDismiss}
            className='mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-cornflower hover:text-white'
          >
            {approval ? 'Go to Workbench' : 'Open in Workbench'}
            <Icons.arrowRight className='h-3.5 w-3.5' strokeWidth={1.5} />
          </Link>
        </div>
        <button
          type='button'
          onClick={onDismiss}
          aria-label='Dismiss notification'
          className='shrink-0 rounded-lg p-1 text-white/50 transition-colors hover:bg-white/10 hover:text-white'
        >
          <Icons.close className='h-3.5 w-3.5' strokeWidth={1.5} />
        </button>
      </div>
    </motion.div>
  )
}

export function RampAlertsProvider({ children }: { children: React.ReactNode }) {
  const [alerts, setAlerts] = useState<RampAlert[]>([])

  /**
   * What this session has already announced.
   *
   * Null until the first poll lands, so the cases that were already there when
   * the page opened are absorbed silently instead of arriving as a pile of
   * notifications nobody asked for.
   */
  const seenCases = useRef<Set<string> | null>(null)
  const seenDecisions = useRef<Set<string>>(new Set())

  const dismiss = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id))
  }, [])

  const markAllRead = useCallback(() => {
    setAlerts((prev) => prev.map((a) => (a.read ? a : { ...a, read: true })))
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return
    if (Notification.permission !== 'default') return
    try {
      void Notification.requestPermission().catch(() => {})
    } catch {
      // Older browsers expose only the callback form. The card still shows.
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    let id: ReturnType<typeof setInterval> | undefined

    const load = () => {
      fetchRampCases(10)
        .then((cases) => {
          if (cancelled || cases.length === 0) return

          const firstPass = seenCases.current === null
          if (firstPass) seenCases.current = new Set()
          const cases_seen = seenCases.current!
          const decisions_seen = seenDecisions.current
          const fresh: RampAlert[] = []

          for (const c of cases) {
            const isNew = !cases_seen.has(c.caseRef)
            cases_seen.add(c.caseRef)

            const decision = openDecisionId(c)
            const decisionIsNew = decision !== null && !decisions_seen.has(decision)
            if (decision) decisions_seen.add(decision)

            if (firstPass) continue
            // A case that arrives already waiting announces the decision, not the
            // arrival: one interruption, and the one that asks for something.
            if (decisionIsNew) {
              fresh.push({
                id: `approval-${decision}`,
                kind: 'approval',
                caseRef: c.caseRef,
                subject: c.subject,
                at: Date.now(),
                read: false,
              })
            } else if (isNew) {
              fresh.push({
                id: `arrival-${c.caseRef}`,
                kind: 'arrival',
                caseRef: c.caseRef,
                subject: c.subject,
                at: Date.now(),
                read: false,
              })
            }
          }

          if (fresh.length === 0) return
          fresh.forEach(notifyIfPermitted)
          setAlerts((prev) => {
            const known = new Set(prev.map((a) => a.id))
            return [...prev, ...fresh.filter((a) => !known.has(a.id))].slice(-3)
          })
        })
        .catch((err) => {
          // The ramp bridge being unavailable is the normal state of this page
          // for most of its life. It costs the notification and nothing else.
          // A 403 is different: the login has no role, and asking again every
          // four seconds only fills the log. Stop until the page reloads.
          if (isForbidden(err) && id !== undefined) {
            clearInterval(id)
            id = undefined
          }
        })
    }

    load()
    id = setInterval(load, POLL_MS)
    return () => {
      cancelled = true
      if (id !== undefined) clearInterval(id)
    }
  }, [])

  const value = useMemo<RampAlertsValue>(
    () => ({
      alerts,
      unread: alerts.filter((a) => !a.read).length,
      dismiss,
      markAllRead,
    }),
    [alerts, dismiss, markAllRead]
  )

  return <RampAlertsContext.Provider value={value}>{children}</RampAlertsContext.Provider>
}

/**
 * The cards that slide in from the top right.
 *
 * They now sit UNDER the bell rather than beside it, and only while unread, so
 * one event is announced once and then lives in the notification list where it
 * can be found again.
 */
export function RampAlertToasts() {
  const { alerts, dismiss } = useRampAlerts()
  const showing = alerts.filter((a) => !a.read).slice(-2)

  if (showing.length === 0) return null

  return (
    <div className='pointer-events-none fixed right-6 top-[4.75rem] z-50 flex flex-col items-end gap-3'>
      <AnimatePresence initial={false}>
        {showing.map((alert) => (
          <div key={alert.id} className='pointer-events-auto'>
            <AlertCard alert={alert} onDismiss={() => dismiss(alert.id)} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  )
}
